import { useState } from "react";
import { Box, Button, Chip, Popover, TextField, Tooltip, Typography } from "@mui/material";
import { Event as EventIcon, WarningAmberRounded as WarningIcon } from "@mui/icons-material";
import { HeaderTriggerButton } from "./HeaderTriggerButton";
import { DATE_FORMATS, formatDate } from "@/utils/formatters/dateFormatter";
import type { ReportedScope } from "../../utils/recordUtils";

/** Reviewer-facing label for a document's carrier-reported claim-type scope. */
const SCOPE_LABEL: Record<ReportedScope, string> = {
  medical: "Medical",
  rx: "Rx",
  combined: "Combined",
};

/** Plain-language explanation of what this control is for — shown on hover and
 *  as the popover subtitle so a first-time reviewer isn't left guessing what
 *  "as-of" means. */
// ADR-051 §2 / #6903: on approve the reported total is authoritative whether or
// not the date is set. The date only decides whether newer claim lines can take
// over later; a reported total with no date is never overridden (#7392).
const WHY =
  "The date the carrier's reported year-to-date total is current through. On approve, the reported total becomes the authoritative value on the claimant tiles. With this date set, the calculated total takes over once claim lines are paid after it; left blank, it can't.";

interface ReportedAsOfControlProps {
  /** Current value as YYYY-MM-DD (empty string when unset). */
  value: string;
  onChange: (value: string) => void;
  /** True when the current value was pre-filled from the document header. */
  fromExtraction: boolean;
  /**
   * Provenance of the prefilled value. Omitted/"header" reads as parsed from the
   * document; "prior_month_end" is a synthesized guess and must say so — telling
   * the reviewer a made-up date came from the Paid Date header invites them to
   * trust it (PR !4427 review F10).
   */
  defaultAsOfSource?: "header" | "prior_month_end" | null;
  /** True when the value is later than today — blocks approval until corrected. */
  isInFuture: boolean;
  /** Today as YYYY-MM-DD (reviewer-local); used as the native picker max. */
  today: string;
  /**
   * True when the chosen as-of is earlier than the latest claim paid date in
   * this document. Non-blocking — approval routes through the confirmation
   * dialog (ADR-045) rather than being disabled.
   */
  isStale?: boolean;
  /** Latest in-doc claim paid date (YYYY-MM-DD) — shown in the stale warning. */
  staleAfterDate?: string;
  /**
   * True when only part of the document's records are loaded (#6755). The
   * stale check scans the LOADED records for the latest claim paid date, so a
   * later date on an unfetched page yields a silent false negative — this
   * turns that into a stated scope, the same treatment search and
   * cascade-exclude got.
   */
  staleCheckPartial?: boolean;
  /**
   * Disables the control. Set when the reviewer lacks documents:edit (ADR-044)
   * or the active partner is read-only (ADR-035, AEGL/ACAP).
   */
  disabled?: boolean;
  /**
   * Carrier-reported claim-type scope of this document (ADR-077 §3). Set only on
   * a carve-out policy, so the reviewer sees which claim type this single as-of
   * covers (Medical / Rx / Combined). undefined => bundled => rendered exactly
   * as before. Display-only — independent of `disabled`.
   */
  scope?: ReportedScope;
  /**
   * "chip" (default) is the toolbar chip. "text" is the review header's text
   * button (#7431, artboard 1a): the date (or "Not set") with a chevron, red
   * when in the future, amber when stale. Same popover either way.
   */
  variant?: "chip" | "text";
  /**
   * Provenance of the current value (#7439 AC4). When set, the popover's
   * source line reads from it — "From the file header." / "Not in the file —
   * defaulted to the month-end before upload." / "Set by you." — instead of
   * the legacy fromExtraction/defaultAsOfSource copy.
   */
  source?: "reviewer" | "file" | "defaulted" | "none";
  /**
   * The rail is showing a paid-through card for this date (#7439 AC1): the
   * text trigger takes the warning tone and icon until the reviewer confirms
   * or changes it. A future date is the error tone regardless.
   */
  flagged?: boolean;
}

/**
 * Document-level "paid-through (as-of) date" for carrier YTD / aggregate
 * (50%-report) documents (ADR-051), presented as a single compact toolbar chip
 * that opens a small editor on click.
 *
 * The chip keeps the review toolbar uncluttered — all explanatory copy, the
 * date input, and the future/stale signals live inside the popover, not the
 * toolbar. The chip itself only signals state at a glance: a call-to-action
 * when unset, the date when set, and error/warning color when the value needs
 * attention.
 *
 * Pre-filled from the header-parsed default when one was found, but ALWAYS
 * editable — the reviewer can enter or correct it manually even when extraction
 * found nothing. A future-dated value blocks approval until corrected; an as-of
 * earlier than the latest claim paid date is a non-blocking warning
 * (approve-after-confirm). Leaving it blank still makes the reported YTD
 * authoritative, with no as-of, so newer claim lines cannot override it
 * (ADR-051 §2, #7392).
 */
export const ReportedAsOfControl = ({
  value,
  onChange,
  fromExtraction,
  defaultAsOfSource,
  isInFuture,
  today,
  isStale,
  staleCheckPartial,
  staleAfterDate,
  disabled,
  scope,
  variant = "chip",
  source,
  flagged = false,
}: ReportedAsOfControlProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  // Scope suffix (carve-out only) — tells the reviewer which claim type this
  // single as-of covers. Empty for bundled (scope undefined) => unchanged label.
  const scopeSuffix = scope ? ` (${SCOPE_LABEL[scope]})` : "";

  // Chip state at a glance: red when future (blocks approval), amber when stale
  // (confirm-to-proceed), neutral-info when set and clean, outlined CTA when
  // unset.
  const chipColor = isInFuture ? "error" : isStale ? "warning" : value ? "info" : "default";
  const chipLabel = value
    ? `As-of${scopeSuffix}: ${formatDate(value)}${isInFuture ? " · future" : ""}`
    : `Set carrier YTD date${scopeSuffix}`;

  // The popover is a title, one line of why, the input and ONE line under it
  // (artboard 3b; walkthrough feedback 2026-10-15: four paragraphs read as
  // noise). The full explanation stays on the trigger's hover tooltip. The
  // line under the input is, by priority: future (error) → earlier than the
  // latest paid claim (warning) → where the value came from → what blank means.
  const partialNote = staleCheckPartial ? " Checked against the loaded records only." : "";
  const provenance = (() => {
    switch (source) {
      case "reviewer":
        return "Set by you.";
      case "defaulted":
        return "Not in the file — defaulted to the month-end before upload.";
      case "file":
        return "From the file header.";
      case "none":
        return null;
      default:
        // Legacy callers (chip variant) without `source`: derive it from the prefill flags.
        if (!value || !fromExtraction) return null;
        return defaultAsOfSource === "prior_month_end"
          ? "Not in the file — defaulted to the month-end before upload."
          : "From the file header.";
    }
  })();
  const line = (() => {
    if (isInFuture) {
      return {
        tone: "error.main",
        text: "Date is in the future — correct it to approve.",
        testId: "paid-through-status",
      };
    }
    if (isStale) {
      return {
        tone: "warning.main",
        text: `Earlier than the latest paid claim${staleAfterDate ? ` (${formatDate(staleAfterDate)})` : ""}.${partialNote}`,
        testId: "paid-through-status",
      };
    }
    if (value && provenance) return { tone: "text.secondary", text: provenance, testId: "paid-through-source" };
    if (!value) {
      return {
        tone: "text.secondary",
        text: "Left blank, the reported total still becomes official and newer claim lines can't take over.",
        testId: "paid-through-status",
      };
    }
    return null;
  })();

  const textLabel = value
    ? `${formatDate(value, DATE_FORMATS.DISPLAY_LONG)}${scopeSuffix}${isInFuture ? " · future" : ""}`
    : `Not set${scopeSuffix}`;
  const warn = isStale || flagged;
  const textColor = isInFuture ? "error.main" : warn ? "warning.dark" : value ? "text.primary" : "warning.dark";
  const showWarningIcon = variant === "text" && !isInFuture && warn && !!value;

  return (
    <>
      <Tooltip title={WHY} arrow>
        {variant === "text" ? (
          <span>
            <HeaderTriggerButton
              onClick={(e) => setAnchorEl(e.currentTarget)}
              disabled={disabled}
              aria-label="Change paid-through date"
              aria-expanded={open}
              color={textColor}
            >
              {showWarningIcon && (
                <WarningIcon
                  sx={{ fontSize: 14, mr: 0.5, verticalAlign: "text-bottom" }}
                  data-testid="paid-through-warning"
                />
              )}
              {textLabel}
            </HeaderTriggerButton>
          </span>
        ) : (
          <Chip
            size="small"
            icon={<EventIcon />}
            label={chipLabel}
            color={chipColor}
            variant={value ? "filled" : "outlined"}
            disabled={disabled}
            onClick={disabled ? undefined : (e) => setAnchorEl(e.currentTarget)}
            aria-label="Carrier YTD as-of date"
          />
        )}
      </Tooltip>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Box sx={{ p: 2, width: 300, display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography variant="subtitle2">Paid-through date{scope ? ` · ${SCOPE_LABEL[scope]}` : ""}</Typography>
          <Typography variant="caption" color="text.secondary">
            Reported YTD becomes the official total as of this date.
          </Typography>
          <TextField
            type="date"
            size="small"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            error={isInFuture}
            autoFocus
            inputProps={{ max: today, "aria-label": "Paid-through date" }}
            fullWidth
          />
          {line && (
            <Typography variant="caption" sx={{ color: line.tone }} data-testid={line.testId}>
              {line.text}
            </Typography>
          )}
          {value && (
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button size="small" onClick={() => onChange("")}>
                Clear
              </Button>
            </Box>
          )}
        </Box>
      </Popover>
    </>
  );
};
